import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  Search, Award, Loader2, ChevronLeft, ChevronRight, ExternalLink,
  Filter, Users, Building2, RefreshCw, MapPin, Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

// Lightweight debouncer (no extra deps)
function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/**
 * Admin → All Listings.
 * Browse every listing in the system with search + filters and toggle
 * the Best of Best flag inline. Claimed listings sort to the top by default.
 */
export default function AdminAllListings() {
  const { getAuthHeader } = useAuth();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [listingType, setListingType] = useState("all"); // all | businesses | individuals
  const [claimedFilter, setClaimedFilter] = useState("all"); // all | claimed | unclaimed
  const [bobFilter, setBobFilter] = useState("all"); // all | bob | not_bob
  const [sort, setSort] = useState("claimed_first"); // claimed_first | newest | name
  const [page, setPage] = useState(1);
  const limit = 25;

  const [data, setData] = useState({ businesses: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({});

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sort,
      });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (listingType !== "all") params.set("listing_type", listingType);
      if (claimedFilter !== "all") params.set("claimed", claimedFilter === "claimed" ? "true" : "false");
      if (bobFilter !== "all") params.set("bob", bobFilter === "bob" ? "true" : "false");

      const res = await axios.get(`${API}/admin/businesses?${params.toString()}`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load listings");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, listingType, claimedFilter, bobFilter, sort, page, getAuthHeader]);

  useEffect(() => { fetchListings(); }, [fetchListings]);
  // Reset to page 1 when any filter changes
  useEffect(() => { setPage(1); }, [debouncedSearch, listingType, claimedFilter, bobFilter, sort]);

  const handleToggleBob = async (biz) => {
    const nextValue = !biz.is_best_of_best;
    setToggling((t) => ({ ...t, [biz.slug]: true }));
    // Optimistic update
    setData((d) => ({ ...d, businesses: d.businesses.map((b) => b.slug === biz.slug ? { ...b, is_best_of_best: nextValue } : b) }));
    try {
      await axios.post(
        `${API}/admin/businesses/${biz.slug}/best-of-best`,
        { is_best_of_best: nextValue },
        { headers: getAuthHeader() },
      );
      toast.success(nextValue ? `${biz.name} is now Best of Best` : `${biz.name} removed from Best of Best`);
    } catch (err) {
      // Revert on failure
      setData((d) => ({ ...d, businesses: d.businesses.map((b) => b.slug === biz.slug ? { ...b, is_best_of_best: !nextValue } : b) }));
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setToggling((t) => { const n = { ...t }; delete n[biz.slug]; return n; });
    }
  };

  const handleDelete = async (biz) => {
    const ok = window.confirm(
      `Delete listing "${biz.name}" (slug: ${biz.slug})?\n\nThis cannot be undone. The public page at /business/${biz.slug} will return 404.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/businesses/${biz.slug}`, { headers: getAuthHeader() });
      setData((d) => ({ ...d, businesses: d.businesses.filter((b) => b.slug !== biz.slug), total: Math.max(0, (d.total || 0) - 1) }));
      toast.success(`Deleted "${biz.name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const totalPages = data.pages || 1;
  const stats = useMemo(() => ({
    total: data.total || 0,
    onPage: data.businesses?.length || 0,
  }), [data]);

  return (
    <div className="space-y-6" data-testid="admin-all-listings">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">All Listings</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">
            Browse every listing in the directory. Claimed listings sort to the top — toggle the gold star to grant Best of Best.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#64748B]">{stats.total.toLocaleString()} total</span>
          <Button onClick={fetchListings} size="sm" variant="outline" className="border-white/10 bg-white/5 hover:bg-white/10 text-white" data-testid="refresh-listings-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl bg-[#0F172A] border border-white/10 p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-5 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, city, slug, email, contact…"
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-[#475569]"
              data-testid="admin-listings-search"
            />
          </div>
          <FilterSelect
            label="Type"
            value={listingType}
            onChange={setListingType}
            options={[
              { v: "all", l: "All Types" },
              { v: "businesses", l: "Businesses" },
              { v: "individuals", l: "Pros / Agents" },
            ]}
            icon={Building2}
            testid="admin-filter-type"
          />
          <FilterSelect
            label="Claim"
            value={claimedFilter}
            onChange={setClaimedFilter}
            options={[
              { v: "all", l: "All Listings" },
              { v: "claimed", l: "Claimed" },
              { v: "unclaimed", l: "Unclaimed" },
            ]}
            icon={Users}
            testid="admin-filter-claimed"
          />
          <FilterSelect
            label="Best of Best"
            value={bobFilter}
            onChange={setBobFilter}
            options={[
              { v: "all", l: "All" },
              { v: "bob", l: "Best of Best" },
              { v: "not_bob", l: "Not Best" },
            ]}
            icon={Award}
            testid="admin-filter-bob"
          />
          <FilterSelect
            label="Sort"
            value={sort}
            onChange={setSort}
            options={[
              { v: "claimed_first", l: "Claimed First" },
              { v: "newest", l: "Newest" },
              { v: "name", l: "A–Z" },
            ]}
            icon={Filter}
            testid="admin-sort"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-[#0F172A] border border-white/10 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="admin-listings-loading">
            <Loader2 className="w-5 h-5 animate-spin text-[#7C3AED]" />
            <span className="ml-2 text-[#94A3B8]">Loading listings…</span>
          </div>
        ) : data.businesses.length === 0 ? (
          <div className="text-center py-16" data-testid="admin-listings-empty">
            <Building2 className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
            <p className="text-white font-medium">No listings match your filters</p>
            <p className="text-[#94A3B8] text-sm mt-1">Try clearing search or changing filters above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02]">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#64748B] border-b border-white/5">
                  <th className="py-3 px-4 w-12">BoB</th>
                  <th className="py-3 px-4">Business</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Location</th>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Open</th>
                </tr>
              </thead>
              <tbody>
                {data.businesses.map((b) => {
                  const ownerBadge = b.is_claimed || b.owner_id;
                  return (
                    <tr key={b.id || b.slug} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`admin-listing-row-${b.slug}`}>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleToggleBob(b)}
                          disabled={!!toggling[b.slug]}
                          className={`w-9 h-9 rounded-lg flex items-center justify-center transition border ${
                            b.is_best_of_best
                              ? "bg-gradient-to-br from-[#F59E0B] to-[#EC4899] border-[#F59E0B]/40 text-white"
                              : "bg-white/5 border-white/10 text-[#64748B] hover:text-[#F59E0B] hover:border-[#F59E0B]/30"
                          }`}
                          aria-label={b.is_best_of_best ? "Remove Best of Best" : "Mark as Best of Best"}
                          title={b.is_best_of_best ? "Remove Best of Best" : "Mark as Best of Best"}
                          data-testid={`toggle-bob-${b.slug}`}
                        >
                          {toggling[b.slug] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3 min-w-[200px]">
                          <img
                            src={b.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(b.name)}&background=7C3AED&color=fff&size=64`}
                            alt={b.name}
                            className="w-9 h-9 rounded-lg object-cover bg-white/5 flex-shrink-0"
                            onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(b.name)}&background=7C3AED&color=fff&size=64`; }}
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-white font-medium truncate max-w-[260px]">{b.name}</p>
                              {ownerBadge && (
                                <span className="px-1.5 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981] text-[10px] border border-[#10B981]/30 flex-shrink-0">
                                  Owner
                                </span>
                              )}
                              {b.is_best_of_best && (
                                <span className="px-1.5 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#FBBF24] text-[10px] border border-[#F59E0B]/30 flex-shrink-0">
                                  BoB
                                </span>
                              )}
                            </div>
                            <p className="text-[#64748B] text-xs truncate max-w-[280px]">/{b.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] border ${
                          b.is_individual
                            ? "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/30"
                            : "bg-[#7C3AED]/10 text-[#A78BFA] border-[#7C3AED]/30"
                        }`}>
                          {b.is_individual ? "Pro / Agent" : "Business"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-[#94A3B8]">
                        <span className="inline-flex items-center gap-1">
                          {(b.city || b.state) ? <MapPin className="w-3 h-3 text-[#64748B]" /> : null}
                          <span className="truncate max-w-[160px]">{[b.city, b.state].filter(Boolean).join(", ") || "—"}</span>
                        </span>
                      </td>
                      <td className="py-3 px-4 text-[#94A3B8] truncate max-w-[140px]">{b.category_name || "—"}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-white/5 text-[#94A3B8] border border-white/10">
                          {b.claim_status || b.status || (b.is_claimed ? "claimed" : "unclaimed")}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-2 justify-end">
                          <a href={`/business/${b.slug}`} target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] hover:text-[#22D3EE] inline-flex items-center gap-1 text-xs" data-testid={`open-listing-${b.slug}`}>
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                          <button
                            onClick={() => handleDelete(b)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md p-1.5 transition"
                            title={`Delete ${b.name}`}
                            data-testid={`delete-listing-${b.slug}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm" data-testid="admin-listings-pagination">
          <span className="text-[#94A3B8]">
            Showing {(page - 1) * limit + 1}–{Math.min(page * limit, stats.total)} of {stats.total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="border-white/10 bg-white/5 hover:bg-white/10 text-white" data-testid="admin-pagination-prev">
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <span className="text-[#94A3B8] text-xs">{page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="border-white/10 bg-white/5 hover:bg-white/10 text-white" data-testid="admin-pagination-next">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, icon: Icon, testid }) {
  return (
    <div className="md:col-span-2 relative">
      {Icon ? <Icon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none" /> : null}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-9 pr-3 py-2 rounded-md bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#7C3AED] appearance-none"
        aria-label={label}
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.v} value={o.v} className="bg-[#0F172A]">{o.l}</option>
        ))}
      </select>
    </div>
  );
}
